/**
 * Global fetch interceptor that detects 401/403 responses and dispatches
 * a custom event so the SessionExpiredDialog can show up.
 *
 * Call `installAuthErrorInterceptor()` once at app startup.
 */

let installed = false;
let lastErrorAt = 0;
const DEBOUNCE_MS = 5000; // Don't spam the dialog

export function installAuthErrorInterceptor() {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch;

  window.fetch = async function (...args: Parameters<typeof fetch>) {
    const response = await originalFetch.apply(this, args);

    // Only intercept Supabase API calls (not external URLs)
    const url = typeof args[0] === "string" ? args[0] : (args[0] as Request)?.url || "";
    const isSupabaseCall = url.includes("supabase.co") || url.includes("supabase.in");

    if (isSupabaseCall && (response.status === 401 || response.status === 403)) {
      const now = Date.now();
      // Debounce to avoid multiple dialogs
      if (now - lastErrorAt > DEBOUNCE_MS) {
        lastErrorAt = now;
        // Don't trigger for auth endpoints themselves (login/signup)
        const isAuthEndpoint = url.includes("/auth/v1/token") || url.includes("/auth/v1/signup");
        if (!isAuthEndpoint) {
          console.warn("[AuthInterceptor] Detected auth error:", response.status, url);
          window.dispatchEvent(new CustomEvent("supabase:auth-error"));
        }
      }
    }

    return response;
  };
}
